import React from 'react';
import { ArrowRight, Check, MessageSquareText, ShieldCheck } from 'lucide-react';
const beforeSignals = ['“You are too sensitive. That never happened.”', 'A confusing thread with no clear pattern to point to.', 'Second-guessing what the conversation actually meant.'];
const afterSignals = ['Potential minimization and reality-denial language flagged.', 'Patterns organized into clear, reviewable signals.', 'Specific moments to revisit with more context and care.'];
export function ComparisonSection() {
  return <section id="comparison" className="border-y border-gray-900 bg-[#050505] py-20 md:py-28" data-id="element-2237" data-mp="2quxrjep61skk">
      <div className="mx-auto max-w-6xl px-6" data-id="element-2238" data-mp="35jlhn5g8ta34">
        <header className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-2239" data-mp="30kqszmvlgrb3">
          <div className="mb-5 inline-flex items-center gap-2 border border-[#FF0000]/30 bg-[#FF0000]/10 px-3 py-1.5" data-id="element-2240" data-mp="3el1ioml63zuo">
            <ShieldCheck size={14} className="text-[#FF0000]" data-id="element-2241" data-mp="3ajzt6xh1z25w" />
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-2242" data-mp="3ahddgak0dgcl">
              Clarity, not guesswork
            </span>
          </div>
          <h2 className="text-4xl font-black leading-tight tracking-tight text-white md:text-5xl" data-id="element-2243" data-mp="3rfqbwtq88vht">
            See the difference between a difficult conversation and a named
            pattern.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-gray-400 md:text-lg" data-id="element-2244" data-mp="59bc4p2j582c">
            ToxicDetector helps turn scattered messages into a structured view
            of the communication signals worth paying attention to.
          </p>
        </header>

        <div className="grid gap-5 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch" data-id="element-2245" data-mp="iz507567nob0">
          <article className="border border-gray-800 bg-[#0A0A0A] p-6 md:p-8" data-id="element-2246" data-mp="nz1474ewrfqk">
            <div className="mb-7 flex items-center gap-3" data-id="element-2247" data-mp="sxksvckvz5mm">
              <span className="flex h-10 w-10 items-center justify-center border border-gray-700 bg-black text-sm font-black text-gray-400" data-id="element-2248" data-mp="sz5mmzzdmkjf">
                01
              </span>
              <div data-id="element-2249" data-mp="89l6y0skvltk">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500" data-id="element-2250" data-mp="891n2ejdancw">
                  Before a scan
                </p>
                <h3 className="mt-1 text-xl font-bold text-white" data-id="element-2251" data-mp="hyv3bsugfqnh">
                  The conversation feels unclear
                </h3>
              </div>
            </div>
            <ul className="space-y-3" data-id="element-2252" data-mp="qz1akwzk7ue7">
              {beforeSignals.map(signal => <li key={signal} className="border border-gray-800 bg-black/40 p-4 text-sm leading-relaxed text-gray-400" data-id="element-2253" data-mp="r0uj6htqgoou">
                  {signal}
                </li>)}
            </ul>
          </article>

          <div className="flex items-center justify-center py-1 lg:py-0" aria-hidden="true" data-id="element-2254" data-mp="vynnf5bh78mh">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF0000]/40 bg-[#050505] text-[#FF0000]" data-id="element-2255" data-mp="32qez793b6kez">
              <ArrowRight size={20} className="rotate-90 lg:rotate-0" data-id="element-2256" data-mp="1d8ht2fc6nzra" />
            </div>
          </div>

          <article className="border border-[#FF0000]/40 bg-[#0A0A0A] p-6 md:p-8" data-id="element-2257" data-mp="1i5i0atkrr8jz">
            <div className="mb-7 flex items-center gap-3" data-id="element-2258" data-mp="13if2elsjaxw4">
              <span className="flex h-10 w-10 items-center justify-center border border-[#FF0000]/40 bg-[#FF0000]/10 text-sm font-black text-[#FF0000]" data-id="element-2259" data-mp="13fsv8n9k2kef">
                02
              </span>
              <div data-id="element-2260" data-mp="uj1s5qwl65ac">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF0000]" data-id="element-2261" data-mp="yik8sahdju7d">
                  After a scan
                </p>
                <h3 className="mt-1 text-xl font-bold text-white" data-id="element-2262" data-mp="kkc8ptp1kcrz">
                  The signals have language
                </h3>
              </div>
            </div>
            <ul className="space-y-3" data-id="element-2263" data-mp="2ewivzqv7mk0n">
              {afterSignals.map(signal => <li key={signal} className="flex gap-3 border border-red-500/20 bg-red-500/5 p-4 text-sm leading-relaxed text-gray-200" data-id="element-2264" data-mp="2jwey1kkck1dk">
                  <Check size={17} className="mt-0.5 shrink-0 text-[#FF0000]" strokeWidth={3} data-id="element-2265" data-mp="eust0owdzlgy" />
                  <span data-id="element-2266" data-mp="ausd0ztn327s">{signal}</span>
                </li>)}
            </ul>
          </article>
        </div>

        <div className="mt-10 border border-gray-800 bg-black p-6 md:flex md:items-center md:justify-between md:gap-8 md:p-8" data-id="element-2267" data-mp="ui1rkr604v9">
          <div className="flex gap-4" data-id="element-2268" data-mp="3s8e8uitozlc8">
            <MessageSquareText className="mt-1 shrink-0 text-[#FFD700]" size={22} data-id="element-2269" data-mp="3s7co1mmvjkhb" />
            <div data-id="element-2270" data-mp="3j90yzp1nouqz">
              <h3 className="text-lg font-bold text-white" data-id="element-2271" data-mp="3j9k76e1i84rt">
                A clearer record for your next decision
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-400" data-id="element-2272" data-mp="3e94rvgxnwn77">
                Review the language, context, and repeated signals in one place.
                Analysis supports reflection; it does not diagnose intent.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center" data-id="element-2273" data-mp="1tpsp2vh352c6">
          <h3 className="text-2xl font-bold text-white" data-id="element-2274" data-mp="1yqzzl2am4fmj">
            Ready for emotional clarity?
          </h3>
          <p className="mt-3 text-sm font-mono text-gray-400" data-id="element-2275" data-mp="22slgcyq9pe20">
            Stop guessing the dynamic. Start naming it.
          </p>
          <a href="/signup" className="mt-7 inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF0000] px-8 py-4 font-black uppercase tracking-wider text-white transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2276" data-mp="163m8hktlieac">
            Start Free Scan
            <ArrowRight size={18} data-id="element-2277" data-mp="nccwn4wcrdfl" />
          </a>
        </div>
      </div>
    </section>;
}